/**
 * Funzioni API per il triage e l'export VEX (aggregation-service, E4 / RF-5).
 * Usano createServiceClient('aggregation') come da pattern slice 1.
 */

import { createServiceClient } from '../../api/client.js';
import type {
  AnalysisResult,
  CloseTicketInput,
  CreateTicketInput,
  CustomerNoteSettings,
  KnownCve,
  KnownCveSearchResult,
  Page,
  Ticket,
  TicketStatus,
  PromoteTicketResult,
  TriageItem,
  UpdateAnalysisInput,
} from './types.js';
import type { ReportCategory } from '../srp/category.js';

/** pageSize di default per la lista triage: il portale non ha ancora controlli UI di paginazione,
 * quindi richiede la pagina massima consentita (clamp lato server, `@tenaciousc/http-kit`) per
 * continuare a mostrare l'intera lista in un'unica pagina. */
const DEFAULT_TRIAGE_PAGE_SIZE = 200;

/** Client parzialmente applicato per l'aggregation-service. */
const aggregationApi = createServiceClient('aggregation');

/** Client parzialmente applicato per il product-service (Ticket). */
const productApi = createServiceClient('product');

/**
 * Recupera la lista triage dei finding per una release (projectUuid DT), ordinata dal server
 * secondo l'ordinamento di default (US-2.7.2/US-2.8.1: knownExploited → no-VEX → cvssScore desc).
 * GET /releases/:projectUuid/triage?page=&pageSize= (Task 3, `@tenaciousc/http-kit`).
 */
export function fetchTriage(
  projectUuid: string,
  page = 1,
  pageSize = DEFAULT_TRIAGE_PAGE_SIZE,
): Promise<Page<TriageItem>> {
  return aggregationApi<Page<TriageItem>>(
    `/releases/${projectUuid}/triage?page=${page}&pageSize=${pageSize}`,
  );
}

/**
 * Aggiorna l'analisi di un finding.
 * POST /releases/:projectUuid/triage
 * Può rispondere 422 se state o justification non sono valori CycloneDX.
 */
export function updateAnalysis(
  projectUuid: string,
  input: UpdateAnalysisInput,
): Promise<AnalysisResult> {
  return aggregationApi<AnalysisResult>(`/releases/${projectUuid}/triage`, {
    method: 'POST',
    body: input,
  });
}

/**
 * Scarica il documento VEX CycloneDX per una release.
 * GET /releases/:projectUuid/vex
 * Restituisce il JSON grezzo (unknown) perché la struttura VEX è opaca per il portale.
 */
export function fetchVex(projectUuid: string): Promise<unknown> {
  return aggregationApi<unknown>(`/releases/${projectUuid}/vex`);
}

// ---------------------------------------------------------------------------
// Ticket — product-service
// ---------------------------------------------------------------------------

/**
 * Contatori sidebar { open, inTriage } per i Ticket.
 * GET /products/triages/counts
 */
export function fetchTicketCounts(): Promise<{ open: number; inTriage: number }> {
  return productApi<{ open: number; inTriage: number }>(`/products/triages/counts`);
}

/**
 * Lista dei Ticket (vista #by-product). Filtra per stato (default: open).
 * GET /products/triages?status=open
 */
export function fetchTickets(status: TicketStatus = 'open'): Promise<Ticket[]> {
  return productApi<Ticket[]>(`/products/triages?status=${encodeURIComponent(status)}`);
}

/**
 * Dettaglio di un Ticket.
 * GET /products/triages/:id
 */
export function fetchTicket(id: number): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}`);
}

/**
 * Aggiorna le note analista di un Ticket.
 * PATCH /products/triages/:id/notes
 * `expectedNotes` = note su cui l'utente ha iniziato a scrivere: se nel frattempo qualcun altro le
 * ha cambiate il server risponde 409 (`NOTES_CONFLICT`) invece di sovrascriverle.
 */
export function updateTicketNotes(
  id: number,
  analystNotes: string,
  expectedNotes?: string,
): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/notes`, {
    method: 'PATCH',
    body: { analystNotes, ...(expectedNotes !== undefined ? { expectedNotes } : {}) },
  });
}

/**
 * Promuove un Ticket a bozza SRP della categoria scelta (Actively Exploited Vulnerability /
 * Severe Incident): i timer SLA partono, le note di analisi passano sul report e viene inviata
 * una notifica email interna (nessuna trasmissione a ENISA).
 * POST /products/triages/:id/promote
 */
export function promoteTicket(id: number, category: ReportCategory): Promise<PromoteTicketResult> {
  return productApi<PromoteTicketResult>(`/products/triages/${id}/promote`, {
    method: 'POST',
    body: { category },
  });
}

/**
 * Presa in carico: open → in_triage. L'utente autenticato diventa il triage lead.
 * POST /products/triages/:id/start
 */
export function startTicket(id: number): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/start`, { method: 'POST' });
}

/**
 * Abbandono ("jump off"): in_triage → open (solo lead). Notifica il system admin.
 * POST /products/triages/:id/leave
 */
export function leaveTicket(id: number): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/leave`, { method: 'POST' });
}

/**
 * Aggiunge un collaboratore (solo lead).
 * POST /products/triages/:id/collaborators
 */
export function addTicketCollaborator(id: number, person: string): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/collaborators`, {
    method: 'POST',
    body: { person },
  });
}

/**
 * Rimuove un collaboratore (solo lead).
 * DELETE /products/triages/:id/collaborators/:person
 */
export function removeTicketCollaborator(id: number, person: string): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/collaborators/${encodeURIComponent(person)}`, {
    method: 'DELETE',
  });
}

/**
 * Chiusura anticipata "non impattato": in_triage → closed (lead/collaboratore). Nessun SRP. Ticket
 * manuale: `customerNote` obbligatoria (almeno N parole), salvata e inviata via email a chi ha
 * segnalato (best-effort). POST /products/triages/:id/close
 */
export function closeTicket(id: number, input: CloseTicketInput): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/close`, {
    method: 'POST',
    body: input.customerNote ? input : { reason: input.reason },
  });
}

/** Parole minime della nota per il cliente in vigore (GET /admin/ticket-settings). */
export function fetchCustomerNoteSettings(): Promise<CustomerNoteSettings> {
  return productApi<CustomerNoteSettings>('/admin/ticket-settings');
}

/**
 * Riapertura: closed → open (ticket chiuso mai promosso a report ENISA). Il server notifica gli
 * amministratori e l'ex lead/collaboratori e registra l'operazione nel log audit.
 * POST /products/triages/:id/reopen
 */
export function reopenTicket(id: number, reason?: string): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/reopen`, {
    method: 'POST',
    body: reason ? { reason } : {},
  });
}

/**
 * Aggiorna lo stato VEX di una singola CVE del triage (resta nel mondo triage).
 * PATCH /products/triages/:id/cves/:cveId
 */
export function updateTicketCveState(
  id: number,
  cveId: string,
  analysisState: string,
): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/cves/${encodeURIComponent(cveId)}`, {
    method: 'PATCH',
    body: { analysisState },
  });
}

/**
 * Crea un Ticket manuale su una o più release del catalogo (nasce senza CVE).
 * POST /products/triages
 */
export function createTicket(input: CreateTicketInput): Promise<Ticket> {
  return productApi<Ticket>('/products/triages', { method: 'POST', body: input });
}

/**
 * Assegna una CVE nota a un Ticket manuale.
 * POST /products/triages/:id/cves
 */
export function assignTicketCve(id: number, cve: KnownCve): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/cves`, {
    method: 'POST',
    body: {
      cveId: cve.cveId,
      ...(cve.severity ? { severity: cve.severity } : {}),
      knownExploited: cve.knownExploited,
    },
  });
}

/**
 * Rimuove una CVE da un Ticket manuale.
 * DELETE /products/triages/:id/cves/:cveId
 */
export function unassignTicketCve(id: number, cveId: string): Promise<Ticket> {
  return productApi<Ticket>(`/products/triages/${id}/cves/${encodeURIComponent(cveId)}`, {
    method: 'DELETE',
  });
}

/**
 * Ricerca CVE note (typeahead) nel DB vulnerabilità di Dependency-Track, lato server:
 * al più `limit` risultati, mai l'intero catalogo. `signal` annulla le richieste superate.
 * GET /cve/search?q=&limit=
 */
export function searchKnownCves(
  q: string,
  limit = 10,
  signal?: AbortSignal,
): Promise<KnownCveSearchResult> {
  const qs = new URLSearchParams({ q, limit: String(limit) });
  return aggregationApi<KnownCveSearchResult>(`/cve/search?${qs.toString()}`, { signal });
}
